import { CardVariant } from "../../../models/CardVariant";
import { SearchTextMatchMethod } from "../../../models/SearchTextMatchMethod";
import { IFilterCommand } from "../IFilterCommand";

export type TextFilterArgs = {
    text: TokenizedText[],
    name: TokenizedText[],
    effect: TokenizedText[],
    matchMethod: SearchTextMatchMethod
}

export class TextFilter<TItem extends CardVariant> implements IFilterCommand<TItem, TextFilterArgs> {
    isMatch(item: TItem, args: TextFilterArgs = { text: [], effect: [], name: [], matchMethod: "all" }): boolean {
        // No text was provided, so not filtering by name.
        if (!args.text?.length && !args.name?.length && !args.effect?.length) {
            return true;
        }

        let match: boolean = true;

        const name = this.normalize(item?.card?.name ?? "");
        const abilityText = this.normalize(item?.card?.normalizedEffectText ?? "");
        const serial = this.normalize(item?.fullSerial ?? "");

        if (args?.text?.length) {
            switch (args.matchMethod) {
                case "all":
                    match = this.searchAll(args.text, name, abilityText, serial, match);
                    break;
                case "effect text":
                    match = this.searchEffectText(args.text, abilityText, serial, match);
                    break;
                case "name":
                    match = this.searchName(args.text, name, serial, match);
                    break;
                default:
                    match = this.searchAll(args.text, name, abilityText, serial, match);
                    break;
            }

            return match;
        }

        if (args?.name?.length) {
            match = this.searchName(args.name, name, serial, match);
        }

        if (args?.effect?.length) {
            match = this.searchEffectText(args.effect, abilityText, serial, match);
        }

        return match;
    }

    normalize(value: string): string {
        return (value || "").replaceAll("'", "").replaceAll("’", "").toLowerCase().trim();
    }

    tokenize(value: string): TokenizedText[] {
        const result: TokenizedText[] = [];
        let normalizedText = this.normalize(value);
        const searchStrings: string[] = normalizedText.split(';');

        for (const item of searchStrings) {
            if (!item || !item.trim()) {
                continue;
            }

            let normalizedToken = item;

            const isNegated: boolean = item.startsWith('!');
            if (isNegated) {
                normalizedToken = item.slice(1, item.length);
            }

            const isStrict = item.startsWith('"') && item.endsWith('"');
            if (isStrict) {
                normalizedToken = item.replaceAll('"', "");
            }

            result.push({
                isNegated: isNegated,
                isStrict: isStrict,
                text: normalizedToken
            });
        }

        return result;
    }

    isNormalizedTokenMatch(props: {
        text: string,
        searchText: string,
        isStrict: boolean,
        matchEntireText: boolean
    }) {
        if (props.isStrict) {
            if (props.matchEntireText) {
                return props.text === props.searchText;
            }

            return (
                props.text === props.searchText
                || props.text?.startsWith(`${props.searchText} `)
                || props.text?.endsWith(` ${props.searchText}`)
                || props.text?.includes(` ${props.searchText} `)
                || false
            );
        } else {
            return (
                props.text.includes(props.searchText)
                || false
            );
        }
    }

    private searchAll(text: TokenizedText[], name: string, abilityText: string, serial: string, match: boolean) {
        let isMatch = match;

        text.forEach(token => {
            let singleSearchStringMatch = false;

            singleSearchStringMatch = (
                this.searchSerial(token, serial)
                || this.isNormalizedTokenMatch({
                    text: name,
                    searchText: token.text,
                    isStrict: token.isStrict,
                    matchEntireText: true
                })
                || this.isNormalizedTokenMatch({
                    text: abilityText,
                    searchText: token.text,
                    isStrict: token.isStrict,
                    matchEntireText: false
                })
                || false
            );

            isMatch = isMatch && (token.isNegated ? !singleSearchStringMatch : singleSearchStringMatch);
        });

        return isMatch;
    }

    private searchName(text: TokenizedText[], name: string, serial: string, match: boolean) {
        let isMatch = match;

        text.forEach(token => {
            let singleSearchStringMatch = false;

            singleSearchStringMatch = this.isNormalizedTokenMatch({
                text: name,
                searchText: token.text,
                isStrict: token.isStrict,
                matchEntireText: true
            });

            singleSearchStringMatch = singleSearchStringMatch || this.searchSerial(token, serial);

            isMatch = isMatch && (
                token.isNegated
                    ? !singleSearchStringMatch
                    : singleSearchStringMatch
            );
        });

        return isMatch;
    }

    private searchEffectText(text: TokenizedText[], abilityText: string, serial: string, match: boolean) {
        let isMatch = match;

        text.forEach(token => {
            let singleSearchStringMatch = false;

            singleSearchStringMatch = this.isNormalizedTokenMatch({
                text: abilityText,
                searchText: token.text,
                isStrict: token.isStrict,
                matchEntireText: false
            });

            singleSearchStringMatch = singleSearchStringMatch || this.searchSerial(token, serial);

            isMatch = isMatch && (
                token.isNegated
                    ? !singleSearchStringMatch
                    : singleSearchStringMatch
            );
        });

        return isMatch;
    }

    private searchSerial(token: TokenizedText, serial: string) {
        return this.isNormalizedTokenMatch({
            text: serial,
            searchText: token.text,
            isStrict: token.isStrict,
            matchEntireText: false
        });
    }
}

export type TokenizedText = {
    text: string,
    isNegated: boolean,
    isStrict: boolean
}